import {
    ESSENCE_RING_MAX,
    findSkillCatalogEntry,
    SKILL_LEVEL_MAX,
    VEIL_ATTUNEMENT_MILESTONE_PERCENTS,
    resolveBasePraxisToAdvance,
    isSkillActive,
    resolveEssenceRing,
    resolvePraxisToAdvance,
    resolveSkillDecayRisk,
    type CharacterDisciplines,
    type CharacterEquipmentLoadout,
    type Skill,
    type SkillBranch,
    type SkillDecayRisk,
} from '@sorrem/shared';
import {
    SKILL_TREE_DIALS,
    SKILL_TREE_GROUPS,
    SKILL_TREE_NODES,
    type SkillTreeDialPlacement,
    type SkillTreeGroupPlacement,
    type SkillTreeNodePlacement,
} from './skill-tree-layout';
import type { UISigilEnhancements } from '@sorrem-client/types';
import { clamp01 } from './skill-tree-math';

/** Weighted recent praxis (`resolveRecentTraining`) that makes a link pulse at full strength. */
export const SKILL_TREE_FULL_PULSE_PRAXIS = 300;

/** A named tier a dial reaches at `percent`. */
export interface SkillTreeDialTier {
    readonly label: string;
    readonly percent: number;
}

/** Martial Discipline tier names by threshold; Raw, below the first, is where every character starts. */
export const SKILL_TREE_MARTIAL_TIERS: readonly SkillTreeDialTier[] = [
    { label: 'Drilled', percent: 20 },
    { label: 'Honed', percent: 35 },
    { label: 'Tempered', percent: 55 },
    { label: 'Masterful', percent: 80 },
];

/** Crafting tier names by threshold. */
export const SKILL_TREE_CRAFTING_TIERS: readonly SkillTreeDialTier[] = [
    { label: 'Apprentice', percent: 10 },
    { label: 'Journeyman', percent: 30 },
    { label: 'Master', percent: 60 },
    { label: 'Artisan', percent: 100 },
];

/** How one branch of the tree measures its skills and its dial. */
export interface SkillTreeBranchRules {
    /** Steps on its skills' level markers: Martial segments, Essence rings, Crafting tiers. */
    readonly maxStep: number;
    /** Its dial's named tiers, when it has them (Veil Attunement has numbered milestones instead). */
    readonly tiers?: readonly SkillTreeDialTier[];
    /** Dial milestone studs, as percents. */
    readonly studs: readonly number[];
    /** False while the branch has not awakened (Crafting in alpha): drawn faint, with no progress. */
    readonly isAwakened: boolean;
    /** Whether its skills advance by praxis (Martial) rather than by skill points (Essence, #145). */
    readonly isPraxisTrained: boolean;
    /** The step a skill at `level` stands on. */
    readonly resolveStep: (level: number) => number;
    /** Its dial's mastery, 0 to 100. */
    readonly resolveDialPercent: (disciplines: CharacterDisciplines) => number;
}

/** Each branch's rules; a new branch must say how it measures its skills and dial. */
export const SKILL_TREE_BRANCH_RULES: Readonly<Record<SkillBranch, SkillTreeBranchRules>> = {
    martial: {
        maxStep: SKILL_LEVEL_MAX,
        tiers: SKILL_TREE_MARTIAL_TIERS,
        studs: SKILL_TREE_MARTIAL_TIERS.map((tier) => tier.percent),
        isAwakened: true,
        isPraxisTrained: true,
        resolveStep: (level) => Math.min(level, SKILL_LEVEL_MAX),
        // The shared rule (#262), as gear requirements read it.
        resolveDialPercent: (disciplines) => disciplines.martialDiscipline,
    },
    essence: {
        maxStep: ESSENCE_RING_MAX,
        studs: VEIL_ATTUNEMENT_MILESTONE_PERCENTS,
        isAwakened: true,
        // Essence skills are raised ring by ring with skill points, not praxis (#145).
        isPraxisTrained: false,
        resolveStep: resolveEssenceRing,
        // Veil Attunement grows with the essence spent on abilities (#145).
        resolveDialPercent: (disciplines) => disciplines.veilAttunement,
    },
    crafting: {
        maxStep: 4,
        tiers: SKILL_TREE_CRAFTING_TIERS,
        studs: SKILL_TREE_CRAFTING_TIERS.map((tier) => tier.percent),
        // Crafting has no skills or mastery in alpha: its skills are shown, faint, with no progress.
        isAwakened: false,
        isPraxisTrained: false,
        resolveStep: () => 0,
        resolveDialPercent: () => 0,
    },
};

export interface SkillTreeNodeModel {
    readonly placement: SkillTreeNodePlacement;
    readonly name: string;
    /** Steps reached: a Martial level, an Essence level, or a Crafting tier. */
    readonly step: number;
    readonly maxStep: number;
    /** Progress toward the next step, 0 to 1 (0 once mastered). */
    readonly progress: number;
    readonly isMastered: boolean;
    /** Equipment-gated and the right weapon isn't equipped. */
    readonly isInactive: boolean;
    readonly decayRisk: SkillDecayRisk;
    /** Its branch hasn't awakened yet (Crafting in alpha): drawn faint, with no progress. */
    readonly isUnawakened: boolean;
    /** Something in a Crucible trains it today; false while its rules are still to be designed. */
    readonly isTrained: boolean;
    /** The character's skill, when it has one. */
    readonly skill: Skill | undefined;
    /** How much it trained over the last few runs, 0 to 1: drives its link pulse. */
    readonly recentTraining: number;
}

/** A grouping root: its name and the skills that hang from it. */
export interface SkillTreeGroupModel {
    readonly placement: SkillTreeGroupPlacement;
    readonly nodes: readonly SkillTreeNodeModel[];
    /** Every skill in it is unawakened. */
    readonly isUnawakened: boolean;
}

export interface SkillTreeDialModel {
    readonly placement: SkillTreeDialPlacement;
    /** Branch mastery, 0 to 100. */
    readonly percent: number;
    readonly studs: readonly { readonly percent: number; readonly isReached: boolean }[];
    /** The branch has nothing to measure yet (Crafting in alpha). */
    readonly isUnawakened: boolean;
}

export interface SkillTreeModel {
    readonly nodes: readonly SkillTreeNodeModel[];
    readonly groups: readonly SkillTreeGroupModel[];
    readonly dials: readonly SkillTreeDialModel[];
    readonly sigil: UISigilEnhancements;
}

function buildNode(
    placement: SkillTreeNodePlacement,
    skill: Skill | undefined,
    equipped: CharacterEquipmentLoadout,
    recentPraxis: number,
): SkillTreeNodeModel {
    const rules = SKILL_TREE_BRANCH_RULES[placement.branch];
    const maxStep = rules.maxStep;
    const recentTraining = clamp01(recentPraxis / SKILL_TREE_FULL_PULSE_PRAXIS);
    const isTrained = findSkillCatalogEntry(placement.id)?.isTrained ?? false;
    if (!rules.isAwakened) {
        return {
            placement, name: skill?.name ?? placement.name, step: 0, maxStep, progress: 0, isMastered: false,
            isInactive: false, decayRisk: 'none', isUnawakened: true, isTrained, skill, recentTraining: 0,
        };
    }

    const step = rules.resolveStep(skill?.level ?? 1);
    const isMastered = step >= maxStep;
    const threshold = skill ? resolvePraxisToAdvance(skill) : resolveBasePraxisToAdvance(1);
    const progress = !rules.isPraxisTrained || isMastered || !skill || threshold === 0 ? 0 : clamp01(skill.praxis / threshold);

    return {
        placement,
        name: skill?.name ?? placement.name,
        step,
        maxStep,
        progress,
        isMastered,
        isInactive: skill ? !isSkillActive(skill, equipped) : false,
        decayRisk: skill ? resolveSkillDecayRisk(skill) : 'none',
        isUnawakened: false,
        isTrained,
        skill,
        recentTraining,
    };
}

function buildDial(placement: SkillTreeDialPlacement, disciplines: CharacterDisciplines): SkillTreeDialModel {
    const rules = SKILL_TREE_BRANCH_RULES[placement.branch];
    const percent = rules.resolveDialPercent(disciplines);
    return {
        placement,
        percent,
        studs: rules.studs.map((stud) => ({ percent: stud, isReached: percent >= stud })),
        isUnawakened: !rules.isAwakened,
    };
}

/**
 * The sigil grows with each dial's milestones: Martial tiers past Raw, Veil
 * milestones, and Crafting tiers (docs/gameplay/skill-tree-ui.md, Sigil).
 */
function buildSigilEnhancements(dials: readonly SkillTreeDialModel[]): UISigilEnhancements {
    const reached = (branch: SkillBranch): number =>
        dials.find((dial) => dial.placement.branch === branch)?.studs.filter((stud) => stud.isReached).length ?? 0;
    return { martialTier: reached('martial'), veilMilestones: reached('essence'), craftingStage: reached('crafting') };
}

/** A character with no Veil Attunement or Martial Discipline yet. */
const NO_DISCIPLINES: CharacterDisciplines = { veilAttunement: 0, martialDiscipline: 0 };

/**
 * Turns a character's skills, disciplines (Veil Attunement and Martial
 * Discipline, 0-100%, as `resolveWearerDisciplines` gives them) and recent
 * training (weighted praxis by skill id, `resolveRecentTraining`) into what
 * each node and dial on the tree draws.
 */
export function buildSkillTreeModel(
    skills: readonly Skill[],
    equipped: CharacterEquipmentLoadout,
    disciplines: CharacterDisciplines = NO_DISCIPLINES,
    recentTraining: Readonly<Record<string, number>> = {},
): SkillTreeModel {
    const skillById = new Map(skills.map((skill) => [skill.id, skill]));
    const nodes = SKILL_TREE_NODES.map((placement) => buildNode(placement, skillById.get(placement.id), equipped, recentTraining[placement.id] ?? 0));
    const groups = SKILL_TREE_GROUPS.map((placement): SkillTreeGroupModel => {
        const members = nodes.filter((node) => node.placement.group === placement.id);
        return { placement, nodes: members, isUnawakened: members.every((node) => node.isUnawakened) };
    });
    const dials = SKILL_TREE_DIALS.map((placement) => buildDial(placement, disciplines));
    return { nodes, groups, dials, sigil: buildSigilEnhancements(dials) };
}
